import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { InfoOutlined, WarningAmber } from '@mui/icons-material';

/**
 * How loudly a banner speaks. Owned here rather than in `weibullDecline` because it is a
 * presentation concept: three of this component's callers say nothing about decline reasons at
 * all, and none of them should have to import a domain module to type a `severity` variable.
 * `weibullDecline` is a consumer of this, not its owner.
 */
export type WeibullFallbackSeverity = 'warning' | 'info';

interface WeibullFallbackBannerProps {
	message: string;
	// Overridable so distinct warnings stay individually addressable when more than one can be on
	// screen at once. Defaults to the original id, which existing callers and tests rely on.
	testId?: string;
	// Not every fallback is a problem. A 3-parameter fit declined because the data has no minimum
	// life is a statement about the data, not a failure, and amber overstates it (I360A-2883).
	// Defaults to 'warning', so the three callers that do not pass it are visually unchanged.
	severity?: WeibullFallbackSeverity;
}

/**
 * The one owner of severity-to-colour for this banner and anything that has to sit beside it.
 * Exported because the results table renders the same distinction as a bare icon rather than a
 * banner, and two copies of these hexes would let the table and the banner drift apart.
 *
 * The info triple came from `failureDefinition/FdResultsInfoBanner`, which was the same component
 * shape rendered separately; that component now delegates here, so these hexes have one owner. The
 * accent is also the blue `resultsTab/AssetsResults` uses for links, so a user meets one blue across
 * the Results tab. stylovyze exposes no `info` palette entry, which is why these are literals rather
 * than theme tokens — replace them with a token if one ever lands.
 */
export const SEVERITY_PALETTE: Record<WeibullFallbackSeverity, { border: string; accent: string; fill: string }> = {
	warning: { border: '#FCC776', accent: '#FAA21B', fill: 'rgba(250,162,27,0.2)' },
	info: { border: '#9BD5EF', accent: '#0696D7', fill: 'rgba(6,150,215,0.1)' },
};

export const WeibullFallbackBanner = ({
	message,
	testId = 'weibull-fallback-banner',
	severity = 'warning',
}: WeibullFallbackBannerProps): JSX.Element => {
	const colours = SEVERITY_PALETTE[severity];
	const Icon = severity === 'info' ? InfoOutlined : WarningAmber;

	return (
		<Stack
			direction="row"
			data-testid={testId}
			data-severity={severity}
			sx={{ border: `1px solid ${colours.border}`, borderRadius: 1, overflow: 'hidden' }}>
			<Box
				sx={{
					width: 32,
					flexShrink: 0,
					bgcolor: colours.accent,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				<Icon sx={{ color: '#fff', fontSize: 20 }} />
			</Box>
			<Box sx={{ flex: 1, bgcolor: colours.fill, px: 2, py: 1.5 }}>
				<Typography sx={{ fontSize: '0.8125rem', color: '#3C3C3C' }}>{message}</Typography>
			</Box>
		</Stack>
	);
};
